import React from 'react';
import {
  Box,
  Paper,
  Typography,
  Breadcrumbs,
  Link,
  Stack,
  Chip,
  useTheme,
  alpha,
} from '@mui/material';
import {
  NavigateNext as NavigateNextIcon,
  Schedule as ScheduleIcon,
  Upload as UploadIcon,
} from '@mui/icons-material';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { DoctorShiftCsvUploader } from '../components/DoctorShift/DoctorShiftCsvUploader';

/**
 * 医師シフトアップロードページ
 * 
 * 意図：
 * - 医師シフトCSVファイルのアップロード専用ページ
 * - ナビゲーションとレイアウトの統一性
 * - ユーザーフレンドリーなUI/UX
 * 
 * 注意点：
 * - DashboardLayoutでラップされることを前提
 * - レスポンシブデザイン対応
 * 
 * 改善提案：
 * - アップロード履歴の表示
 * - バッチアップロード機能
 * - インポート結果のダウンロード機能
 */
export const DoctorShiftUploadPage: React.FC = () => {
  const theme = useTheme();
  const navigate = useNavigate();

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3 }}
    >
      <Box
        sx={{
          minHeight: '100vh',
          bgcolor: theme.palette.grey[50],
          py: 3,
          maxWidth: 1200,
          mx: 'auto',
        }}
      >
        {/* ヘッダー */}
        <Box sx={{ px: 4, mb: 4 }}>
          <Paper
            elevation={0}
            sx={{
              px: 4,
              py: 3,
              mb: 3,
              borderRadius: 3,
              background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.1)} 0%, ${alpha(theme.palette.primary.light, 0.05)} 100%)`,
              backdropFilter: 'blur(10px)',
              border: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
            }}
          >
            {/* パンくずリスト */}
            <Breadcrumbs
              separator={<NavigateNextIcon fontSize="small" />}
              sx={{ mb: 2 }}
            >
              <Link
                color="inherit"
                href="#"
                onClick={(e: React.MouseEvent) => {
                  e.preventDefault();
                  navigate('/dashboard');
                }}
                sx={{ cursor: 'pointer' }}
              >
                ダッシュボード
              </Link>
              <Link
                color="inherit"
                href="#"
                onClick={(e: React.MouseEvent) => {
                  e.preventDefault();
                  navigate('/doctors');
                }}
                sx={{ cursor: 'pointer' }}
              >
                医師管理
              </Link>
              <Typography color="text.primary">医師シフトCSVアップロード</Typography>
            </Breadcrumbs>

            <Stack direction="row" alignItems="center" justifyContent="space-between">
              <Box>
                <Stack direction="row" alignItems="center" spacing={2} mb={1}>
                  <Typography
                    variant="h4"
                    sx={{
                      fontWeight: 700,
                      background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
                      backgroundClip: 'text',
                      WebkitBackgroundClip: 'text',
                      WebkitTextFillColor: 'transparent',
                    }}
                  >
                    医師シフトCSVアップロード
                  </Typography>
                  <Chip
                    icon={<ScheduleIcon />}
                    label="シフト管理"
                    color="primary"
                    variant="outlined"
                    size="small"
                  />
                </Stack>
                <Typography variant="body2" color="text.secondary">
                  医師の勤務シフト情報をCSVファイルから一括登録できます
                </Typography>
              </Box>
            </Stack>
          </Paper>

          {/* 説明セクション */}
          <Paper
            elevation={0}
            sx={{
              p: 3,
              mb: 3,
              borderRadius: 2,
              border: `1px solid ${theme.palette.divider}`,
              bgcolor: alpha(theme.palette.info.main, 0.02),
            }}
          >
            <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 600 }}>
              CSVファイル形式について
            </Typography>
            <Stack spacing={1}>
              <Typography variant="body2" color="text.secondary">
                • タブ区切り形式のCSVファイルを使用してください
              </Typography>
              <Typography variant="body2" color="text.secondary">
                • 必須項目：医師名、勤務日付、診療開始時間、診療終了時間
              </Typography>
              <Typography variant="body2" color="text.secondary">
                • 日付形式：YYYY/MM/DD（例：2025/07/01）
              </Typography>
              <Typography variant="body2" color="text.secondary">
                • 時刻形式：H:MM または HH:MM（例：9:00、14:30）
              </Typography>
              <Typography variant="body2" color="text.secondary">
                • 同一医師の勤務時間が重複しないようご注意ください
              </Typography>
            </Stack>
          </Paper>

          {/* アップローダーコンポーネント */}
          <Paper
            elevation={0}
            sx={{
              p: 4,
              borderRadius: 3,
              border: `1px solid ${theme.palette.divider}`,
              background: theme.palette.background.paper,
            }}
          >
            <Stack direction="row" alignItems="center" spacing={1} mb={3}>
              <UploadIcon color="primary" />
              <Typography variant="h6" fontWeight={600}>
                ファイルアップロード
              </Typography>
            </Stack>
            
            <DoctorShiftCsvUploader />
          </Paper>
        </Box>
      </Box>
    </motion.div>
  );
};